import PouchDB from 'pouchdb';

// in seconds
const SYNC_INTERVAL = 10;
// Fewer, larger pull batches cut HTTP round trips; the default is 100.
const PULL_REPLICATION_BATCH_SIZE = 500;
const serverDBUrl = process.env.REACT_APP_DB_URL;
const remoteDBName = process.env.REACT_APP_REMOTE_DB_NAME;
const remoteDBUsername = process.env.REACT_APP_REMOTE_DB_USERNAME;
const remoteDBPassword = process.env.REACT_APP_REMOTE_DB_PASSWORD;
const localIncidentDatabaseName = process.env.REACT_APP_LOCAL_DB_NAME;
const incidentDatabaseLockName = localIncidentDatabaseName + "-incident-database";

var dbURL = serverDBUrl + "/" + remoteDBName;

var remoteDB = new PouchDB(dbURL, {auth: {username: remoteDBUsername, password: remoteDBPassword}});
var db = new PouchDB(localIncidentDatabaseName);

// if you want to access it from console, uncomment these 2 lines
// window.remote_debug_db = remoteDB;
// window.debug_db = db;

var databaseMutex = Promise.resolve();
var databaseOperationsAccepted = true;
var synchronizationLifecycleGeneration = 0;
var backgroundIntervalHandle = null;
var backgroundRefreshPromise = null;
var backgroundRefreshGeneration = null;
var backgroundRefreshCallback = null;
var backgroundSyncPaused = true;
var activePushReplication = null;
var activePullReplication = null;
var activeSyncReplication = null;
var activeSyncOperation = null;
const getCrossTabLockManager = () => {
    if (typeof navigator === "undefined" || !navigator.locks ||
        typeof navigator.locks.request !== "function") {
        return null;
    }

    return navigator.locks;
}

const withCrossTabDatabaseLock = (mode, operation) => {
    const lockManager = getCrossTabLockManager();
    if (!lockManager) {
        return Promise.resolve().then(operation);
    }

    return lockManager.request(
        incidentDatabaseLockName,
        {mode},
        (lock) => {
            if (!lock) {
                throw createIncidentPayloadError(
                    "cross_tab_lock_unavailable",
                    "The incident database cross-tab lock was not acquired"
                );
            }

            return operation();
        }
    );
}

const withDatabaseLock = (operation, crossTabLockMode = "shared") => {
    if (!databaseOperationsAccepted) {
        const error = new Error("Database synchronization has been stopped");
        error.name = "database_stopped";
        return Promise.reject(error);
    }

    if (crossTabLockMode === "exclusive" && !getCrossTabLockManager()) {
        return Promise.reject(createIncidentPayloadError(
            "cross_tab_lock_unavailable",
            "Database mutation requires a cross-tab database lock"
        ));
    }

    const queuedOperation = databaseMutex.then(
        () => withCrossTabDatabaseLock(crossTabLockMode, operation),
        () => withCrossTabDatabaseLock(crossTabLockMode, operation)
    );
    databaseMutex = queuedOperation.catch(() => {});
    return queuedOperation;
}

const assertSynchronizationLifecycleActive = (lifecycleGeneration) => {
    if (!databaseOperationsAccepted || lifecycleGeneration !== synchronizationLifecycleGeneration) {
        const error = new Error("Synchronization lifecycle has been stopped");
        error.name = "synchronization_cancelled";
        error.cancelled = true;
        throw error;
    }
}

const validateReplicationResult = (result, direction) => {
    if (result && result.errors && result.errors.length > 0) {
        throw result.errors[0];
    }

    if (!result || result.ok !== true || result.status === "cancelled") {
        const error = new Error(direction + " replication did not complete successfully");
        error.replicationResult = result;
        error.cancelled = Boolean(result && result.status === "cancelled");
        throw error;
    }

    return result;
}

const runReplication = (direction, createReplication) => {
    var replicationHandle = null;

    return new Promise(function(resolve, reject) {
        replicationHandle = createReplication();

        if (direction === "push") {
            activePushReplication = replicationHandle;
        } else if (direction === "pull") {
            activePullReplication = replicationHandle;
        }

        replicationHandle.on('complete', function(result) {
            try {
                resolve(validateReplicationResult(result, direction));
            } catch (err) {
                reject(err);
            }
        }).on('error', function(err) {
            reject(err);
        });
    }).finally(function() {
        if (direction === "push" && activePushReplication === replicationHandle) {
            activePushReplication = null;
        }
        if (direction === "pull" && activePullReplication === replicationHandle) {
            activePullReplication = null;
        }
    });
}

const rawPushAllChanges = () => {
    return runReplication("push", () => db.replicate.to(remoteDB));
}

const createIncidentPayloadError = (name, message) => {
    const error = new Error(message);
    error.name = name;
    return error;
}

const normalizeDocumentIncidentIds = (document) => {
    if (!document || !Array.isArray(document.incident_ids) || document.incident_ids.length === 0) {
        throw createIncidentPayloadError(
            "invalid_incident_ownership",
            "Payload document has no incident ownership"
        );
    }

    const incidentIds = [];
    document.incident_ids.forEach((documentIncidentId) => {
        if (typeof documentIncidentId !== "string" || documentIncidentId.length === 0) {
            throw createIncidentPayloadError(
                "invalid_incident_ownership",
                "Payload document " + document._id + " has invalid incident ownership"
            );
        }

        if (!incidentIds.includes(documentIncidentId)) {
            incidentIds.push(documentIncidentId);
        }
    });

    return incidentIds;
}

export const pushAllChanges = () => {
    return withDatabaseLock(rawPushAllChanges);
}

export const resolveFileSource = (fileId, incidentId) => {
    return withDatabaseLock(async () => {
        const fileDocument = await db.get("file_" + fileId);
        const incidentIds = normalizeDocumentIncidentIds(fileDocument);

        if (typeof incidentId !== "string" || !incidentIds.includes(incidentId)) {
            throw createIncidentPayloadError(
                "invalid_incident_ownership",
                "File document does not belong to the current incident"
            );
        }

        if (typeof fileDocument.serverURL !== "string" || fileDocument.serverURL.length === 0) {
            return null;
        }

        return {
            type: "remote",
            serverURL: fileDocument.serverURL,
        };
    });
}

export const sync = (callback) => {
    const lifecycleGeneration = synchronizationLifecycleGeneration;
    console.log('sync start');
    const operation = Promise.resolve().then(async () => {
        assertSynchronizationLifecycleActive(lifecycleGeneration);
        const startedAt = Date.now();
        const timings = {replicationMs: 0};
        const documents = {
            push: {read: 0, written: 0, failed: 0},
            pull: {read: 0, written: 0, failed: 0},
        };
        var completed = false;
        var syncHandle = null;
        const replicationStartedAt = Date.now();
        const recordProgress = (direction, change) => {
            documents[direction] = {
                read: change.docs_read || 0,
                written: change.docs_written || 0,
                failed: change.doc_write_failures || 0,
            };
        };
        try {
            const result = await new Promise((resolve, reject) => {
                syncHandle = db.sync(remoteDB, {pull: {batch_size: PULL_REPLICATION_BATCH_SIZE}});
                activeSyncReplication = syncHandle;
                syncHandle.on('change', (event) => {
                    recordProgress(event.direction, event.change);
                    console.log('PouchDB synchronization progress', {
                        direction: event.direction,
                        documents: documents[event.direction],
                        elapsedMs: Date.now() - startedAt,
                    });
                }).on('complete', (result) => {
                    try {
                        recordProgress('push', result.push);
                        recordProgress('pull', result.pull);
                        validateReplicationResult(result.push, 'push');
                        validateReplicationResult(result.pull, 'pull');
                        resolve(result);
                    } catch (error) {
                        reject(error);
                    }
                }).on('error', reject);
            });
            assertSynchronizationLifecycleActive(lifecycleGeneration);
            completed = true;
            return result;
        } finally {
            timings.replicationMs = Date.now() - replicationStartedAt;
            if (activeSyncReplication === syncHandle) {
                activeSyncReplication = null;
            }
            console.log('POS1 document synchronization profile', {
                completed,
                elapsedMs: Date.now() - startedAt,
                documents,
                timings,
            });
        }
    }).then(async (result) => {
        if (callback) {
            await callback();
        }
        return result;
    }, async (error) => {
        if (callback) {
            await callback(error);
        }
        throw error;
    });
    const guardedOperation = operation.finally(() => {
        if (activeSyncOperation === guardedOperation) {
            activeSyncOperation = null;
        }
    });
    activeSyncOperation = guardedOperation;
    return guardedOperation;
}

const executeBackgroundRefresh = (lifecycleGeneration) => {
    if (backgroundSyncPaused) {
        return Promise.resolve(null);
    }

    assertSynchronizationLifecycleActive(lifecycleGeneration);
    return sync().then(function(result) {
        assertSynchronizationLifecycleActive(lifecycleGeneration);
        const applicationRefresh = backgroundRefreshCallback;
        if (applicationRefresh) {
            return Promise.resolve(applicationRefresh()).then(() => result);
        }

        return result;
    }, function(err) {
        if (err && (err.cancelled || err.name === "database_stopped" ||
            err.name === "synchronization_cancelled")) {
            throw err;
        }

        const applicationRefresh = backgroundRefreshCallback;
        if (!applicationRefresh) {
            throw err;
        }

        return Promise.resolve(applicationRefresh(err)).then(function() {
            throw err;
        }, function(applicationRefreshError) {
            if (err && typeof err === "object") {
                err.applicationRefreshError = applicationRefreshError;
            }
            throw err;
        });
    });
}

const trackBackgroundRefresh = (refreshPromise, lifecycleGeneration) => {
    const guardedPromise = refreshPromise.finally(function() {
        if (backgroundRefreshPromise === guardedPromise) {
            backgroundRefreshPromise = null;
            backgroundRefreshGeneration = null;
        }
    });
    backgroundRefreshPromise = guardedPromise;
    backgroundRefreshGeneration = lifecycleGeneration;

    return guardedPromise;
}

const runBackgroundRefresh = () => {
    if (backgroundSyncPaused) {
        return Promise.resolve(null);
    }

    const lifecycleGeneration = synchronizationLifecycleGeneration;
    if (backgroundRefreshPromise) {
        if (backgroundRefreshGeneration === lifecycleGeneration) {
            return backgroundRefreshPromise;
        }

        const drainingRefresh = backgroundRefreshPromise;
        const chainedRefresh = drainingRefresh
            .catch(() => {})
            .then(() => executeBackgroundRefresh(lifecycleGeneration));
        return trackBackgroundRefresh(chainedRefresh, lifecycleGeneration);
    }

    return trackBackgroundRefresh(
        executeBackgroundRefresh(lifecycleGeneration),
        lifecycleGeneration
    );
}

export const resumeLocalDocuments = () => {
    databaseOperationsAccepted = true;
    return withDatabaseLock(() => Promise.resolve());
}

export const startSync = (callback) => {
    databaseOperationsAccepted = true;
    const lifecycleGeneration = synchronizationLifecycleGeneration;
    if (callback) {
        backgroundRefreshCallback = callback;
    }

    const startBackgroundSync = () => {
        assertSynchronizationLifecycleActive(lifecycleGeneration);
        backgroundSyncPaused = false;

        // now it seems that the app in BG, does not trigger the intervall callback
        if (!backgroundIntervalHandle) {
            backgroundIntervalHandle = setInterval(
                function() {
                    runBackgroundRefresh().catch(function(err) {
                        console.log('sync error');
                        console.log(err);
                    });
                },
                SYNC_INTERVAL * 1000
            );
        }

        return runBackgroundRefresh();
    }

    return startBackgroundSync();
}

export const pauseSync = () => {
    backgroundSyncPaused = true;

    if (backgroundIntervalHandle) {
        clearInterval(backgroundIntervalHandle);
        backgroundIntervalHandle = null;
    }

    const activeCycle = backgroundRefreshPromise;
    return activeCycle || Promise.resolve();
}

export const stopSync = () => {
    databaseOperationsAccepted = false;
    synchronizationLifecycleGeneration += 1;
    backgroundSyncPaused = true;
    backgroundRefreshCallback = null;

    if (backgroundIntervalHandle) {
        clearInterval(backgroundIntervalHandle);
        backgroundIntervalHandle = null;
    }

    const activeCycle = backgroundRefreshPromise || activeSyncOperation;
    const acceptedOperations = databaseMutex;
    if (activeSyncReplication && activeSyncReplication.cancel) {
        activeSyncReplication.cancel();
    }
    if (activePullReplication && activePullReplication.cancel) {
        activePullReplication.cancel();
    }

    if (activeCycle) {
        return activeCycle.then(() => acceptedOperations, (err) => {
            return acceptedOperations.then(() => {
                if (!err.cancelled) {
                    throw err;
                }
            });
        });
    }

    return acceptedOperations;
}

export const getP = (id) => {
    return withDatabaseLock(() => db.get(id));
}

export const get = (id, callback, errCallback) => {
    //console.log("get data from id, " + id);
    return withDatabaseLock(() => db.get(id)).then(doc => {
        // console.log(doc);
        callback(doc);
        return doc;
    }).catch(err => {
        if (err.name === "database_stopped") {
            throw err;
        }

        if (errCallback) {
            return errCallback(err);
        }
    });
}

const mergeIncidentIds = (...incidentIdLists) => {
    const incidentIds = [];

    incidentIdLists.forEach((incidentIdList) => {
        if (Array.isArray(incidentIdList)) {
            incidentIdList.forEach((incidentId) => {
                if (!incidentIds.includes(incidentId)) {
                    incidentIds.push(incidentId);
                }
            });
        }
    });

    return incidentIds;
}

const putDocument = (id, data, incidentId, rejectErrors = false) => {
    //console.log("put data to id, " + id);
    const dataToSend = {
        ...data,
        _id: id,
    }

    //console.log(dataToSend);
    return db.get(id)
        .catch(function (err) {
            if (err.name === 'not_found') {
                return dataToSend;
            } else {
                if(rejectErrors) {
                    throw err;
                }

                console.log("error updating document " + id);
                console.log(err);
            }
        })
        .then(function(doc) {
            if(doc._rev) {
                dataToSend._rev = doc._rev;
            }

            if(typeof incidentId !== "undefined") {
                dataToSend.incident_ids = mergeIncidentIds(doc.incident_ids, data.incident_ids, [incidentId]);
            }

            Object.assign(doc, dataToSend);

            return db.put(doc);
        })
        .then(function(resultDoc /* this is a doc from pouch countaining some metadata that the actuall doc has been saved */) {
            return db.get(id);
        })
        .catch(function (err) {
            if(rejectErrors) {
                throw err;
            }

            console.log("error updating document");
            console.log(err);
        });
}

export const put = (id, data, callback) => {
    return withDatabaseLock(() => putDocument(id, data))
        .then((doc) => {
            if (callback) {
                callback(doc);
            }
            return doc;
        });
}

export const putIncidentOwned = (id, data, incidentId, callback) => {
    return withDatabaseLock(() => putDocument(id, data, incidentId, true))
        .then((doc) => {
            if (callback) {
                callback(doc);
            }
            return doc;
        });
}

// awaitable get that rejects on error, unlike `get` which swallows failures.
export const getStrict = (id) => {
    return withDatabaseLock(() => db.get(id));
}

// awaitable put that rejects on error, unlike `put` which swallows failures.
// writes exactly the given data with the `_rev` the caller loaded (none for a
// create), so a concurrent local change surfaces as a 409 conflict instead of
// being rebased onto the latest revision and silently overwriting it.
export const putStrict = (id, data) => {
    return withDatabaseLock(() => {
        const dataToSend = {
            ...data,
            _id: id,
        }

        return db.put(dataToSend)
            .then(function () {
                return db.get(id);
            });

        // prior implementation: re-read and merged onto the current local revision,
        // which rebased the write and could silently overwrite a concurrent change
        // instead of surfacing a 409 conflict.
        // return db.get(id)
        //     .catch(function (err) {
        //         // a missing document is a create, any other error must propagate
        //         if (err.name === 'not_found') {
        //             return dataToSend;
        //         }
        //         throw err;
        //     })
        //     .then(function (doc) {
        //         if (doc._rev) {
        //             dataToSend._rev = doc._rev;
        //         }
        //
        //         Object.assign(doc, dataToSend);
        //         return db.put(doc);
        //     })
        //     .then(function () {
        //         return db.get(id);
        //     });
    });
}

// awaitable outbound replication for a single document that rejects unless the
// remote write is confirmed. `replicate.to` reports per-document write failures
// in the result's `errors` array while still emitting `complete`, so a `complete`
// with a non-empty `errors` array must be treated as a failed push, not success.
export const replicateDocToRemote = (id) => {
    return withDatabaseLock(() => {
        return runReplication("push", () => db.replicate.to(remoteDB, { doc_ids: [id] }));
    });
}

export const getRemoteStrict = (id) => {
    return withDatabaseLock(() => remoteDB.get(id, { conflicts: true }));
}

export const reloadDocFromRemote = (id) => {
    return withDatabaseLock(() => {
        return runReplication("pull", () => remoteDB.replicate.to(db, { doc_ids: [id] }))
            .then(() => db.get(id));
    });
}

export const remove = (id) => {
    //console.log("delete id, " + id);

    return withDatabaseLock(() => {
        return db.get(id).then(function(doc) {
            return db.remove(doc);
        }).then(function (result) {
            console.log("document deleted");
            return result;
        });
    });
}

export const getAllWithPrefix = (prefix) => {
    return withDatabaseLock(() => {
        return _getAll({
            include_docs: true,
            startkey: prefix,
            endkey:  prefix + "\uffff" // this causes all the docs starting with that prefix to be returned
        }, false).then((docs) => {
            const elems = [];

            for(let i = 0; i < docs.rows.length; i++) {
                const doc = docs.rows[i].doc;
            
                elems.push(doc);
            };

            return elems;
        });
    });
}

const _getAll = (src, debug = true, callback = null) => {
    if(debug) {
        console.log("pouch db get all params");
        console.log(src);
    }

    return db.allDocs(src).then(function (res) {
        if(debug) {
            console.log("pouch db get all -> results");
            console.log(res);
        }

        if(callback) {
            callback(res);
        }
        return res;
    }).catch(function (err) {
        console.log("pouch db get all -> error");
        console.log(err);
        throw err;
    });
}
